'use strict';

/**
 * Normalized UI state for a pipeline stage row (derived only from canonical PortalApi fact `status`).
 * Mapping: backend `Started` → `running`, `Completed` → `success`, `Failed` → `failed`,
 * `Skipped` → `skipped`; anything else → `pending`.
 * @typedef {'pending' | 'running' | 'success' | 'failed' | 'skipped'} PipelineStatusState
 */

/**
 * Canonical workflow lane strings (aligned with PortalApi `PipelineStatusWorkflowNames`; extend as producers add lanes).
 * @readonly
 */
export const PipelineStatusWorkflowNames = Object.freeze({
	Publishing: 'Publishing',
	Processing: 'Processing',
	Deletion: 'Deletion',
});

/**
 * Binds a graph node to append-only facts: **lane identity** = `workflow`; **node identity within the lane** = `pipeline` + `stage`;
 * **producer identity** = `service` (Portal `PipelineStatusFactDto.service`). All four are required; matching is trim + exact string equality (case-sensitive).
 * `workflow` is an operational lane, not display metadata. `scope` on the graph definition is display-only (ownership label).
 *
 * @typedef {Object} PipelineStatusFactMatch
 * @property {string} scope display ownership label (e.g. `Project`)
 * @property {string} workflow e.g. `Publishing` (see {@link PipelineStatusWorkflowNames})
 * @property {string} pipeline product pipeline segment label from producers
 * @property {string} stage producer stage string (e.g. topic / middleware stage name)
 * @property {string} service producer name; must be non-empty after trim and must match fact rows for this node
 */

/**
 * Binds a graph node to a declarative iteration manifest row: correlate via the parent stage’s latest fact `correlationId`,
 * then match `PortalApi.PipelineStatusIterationManifestDto` by `parentStage`, `iterationDimension`, and optional `parentService`.
 *
 * @typedef {Object} PipelineStatusIterationManifestMatch
 * @property {string} parentStage must equal `factMatch.stage` on some **other** graph stage (the parent node)
 * @property {string} iterationDimension e.g. `SampleSet` (must match manifest + per-iteration facts)
 * @property {string} [parentService] when set, manifest rows must use the same `parentService`
 */

/**
 * @typedef {'allIterationsSucceeded'} PipelineStatusManifestCompletionPolicy
 */

/**
 * One node in the PipelineStatus **graph definition**: topology (`dependsOn`, stable `id`) plus `factMatch`
 * binding to append-only facts. `description` is display copy only (not used for fact matching).
 * Top-level **`service`** is the producer label shown on the card; it is independent of fact matching (bind key still uses `factMatch.service`).
 *
 * @typedef {Object} PipelineStatusGraphStage
 * @property {string} id
 * @property {string} description
 * @property {string} service producer label for the node (required); must be non-empty after trim
 * @property {string[]} dependsOn
 * @property {PipelineStatusFactMatch} factMatch
 * @property {PipelineStatusIterationManifestMatch} [iterationManifestMatch] when set with {@link PipelineStatusManifestCompletionPolicy}, completion is driven by manifest iteration values + matching facts
 * @property {PipelineStatusManifestCompletionPolicy} [completionPolicy]
 */

/**
 * Static graph definition for the PipelineStatus DAG (nodes + edges implied by `dependsOn`).
 *
 * @typedef {Object} PipelineStatusGraphDefinition
 * @property {string} id
 * @property {PipelineStatusGraphStage[]} stages
 */

/**
 * Row from `GET /api/v1/admin/pipeline-status/projects/{projectId}/facts` (PortalApi `PipelineStatusFactDto`).
 *
 * **workflow** — operational graph lane (`Publishing`, …).
 * **pipeline** / **stage** — product pipeline segment within a workflow. Portal may still return `source`; Aperture does not map or display it.
 *
 * @typedef {Object} PipelineStatusFact
 * @property {string} id
 * @property {string} [scope] ignored for binding; display scope comes from the graph definition
 * @property {string} workflow
 * @property {string} subjectId
 * @property {string} pipeline
 * @property {string} stage
 * @property {'Started' | 'Completed' | 'Failed' | 'Skipped' | string} status SNS / Portal `PipelineStageStatus` enum names; other values are treated as non-canonical.
 * @property {string | undefined} [occurredAt]
 * @property {string | null | undefined} [correlationId]
 * @property {string | undefined} [service]
 * @property {string | undefined} [serviceVersion]
 * @property {string | undefined} [updatedAt] legacy row shape (adapter fallback only)
 * @property {string | null | undefined} [iterationDimension]
 * @property {string | null | undefined} [iterationValue]
 * @property {string | null | undefined} [detailsJson]
 */

/**
 * Declarative iteration manifest from `GET …/facts` (`PipelineStatusIterationManifestDto`). Not execution evidence.
 *
 * @typedef {Object} PipelineStatusIterationManifest
 * @property {string} id
 * @property {string} correlationId
 * @property {string} [scope] ignored for matching; display scope comes from the graph definition
 * @property {string} workflow
 * @property {string} pipeline
 * @property {string} parentStage
 * @property {string | null | undefined} [parentService]
 * @property {string} subjectId
 * @property {string} iterationDimension
 * @property {string[]} iterationValues
 * @property {string} createdAtUtc
 * @property {string} sourceService
 */

/**
 * View-model for one graph node after facts are merged: definition fields except `factMatch`, plus resolved status and fact metadata.
 * **`scope`**, **`workflow`**, and **`service`** always mirror the graph definition (lane + display producer), not the matched fact row.
 *
 * @typedef {Object} PipelineStatusStage
 * @property {string} id
 * @property {string} description
 * @property {string[]} dependsOn
 * @property {PipelineStatusState} status
 * @property {string | undefined} [occurredAt]
 * @property {string | undefined} [factId]
 * @property {string} scope
 * @property {string} workflow
 * @property {string | undefined} [subjectId]
 * @property {string | null | undefined} [correlationId]
 * @property {string} service display producer from graph definition
 * @property {string | undefined} [serviceVersion]
 * @property {string[] | undefined} [logs]
 * @property {string[] | undefined} [artifacts]
 * @property {string[] | undefined} [errors]
 * @property {string | undefined} [startedAt]
 * @property {string | undefined} [completedAt]
 * @property {number | undefined} [iterationCompletedCount] manifest-backed only: count of declared iteration values whose latest matching fact is `Completed`
 * @property {number | undefined} [iterationTotalCount] manifest-backed only: `manifest.iterationValues.length`
 */
